import React, { useEffect, useState } from 'react'
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { FaCircleCheck, FaCircleXmark, FaFilePdf, FaMagnifyingGlass } from 'react-icons/fa6'
import signatureService from '../../services/signature/SignatureService'
import Logo2 from '../../assets/images/BPPMHKP.png'

const stageLabel = stage => stage === 'APPROVAL' ? 'Approval' : stage === 'FINAL' ? 'Final' : stage || '-'

const formatDate = value => value
    ? new Date(value).toLocaleString('id-ID', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })
    : '-'

/**
 * Halaman publik (tanpa login) untuk memverifikasi dokumen bertanda tangan
 * elektronik berdasarkan kode verifikasi / QR. Route: /signature/verify/:verificationCode
 */
export default function VerifySignaturePage() {
    const { verificationCode } = useParams()
    const navigate = useNavigate()

    const [code, setCode] = useState(verificationCode || '')
    const [result, setResult] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        setCode(verificationCode || '')
        setResult(null)
        setError('')
        if (!verificationCode) {
            return
        }

        let active = true
        setLoading(true)
        signatureService.verify(verificationCode)
            .then(res => { if (active) setResult(res.data) })
            .catch(e => {
                if (active) setError(e?.response?.data?.message || 'Verifikasi tidak dapat diproses. Silakan coba lagi.')
            })
            .finally(() => { if (active) setLoading(false) })
        return () => { active = false }
    }, [verificationCode])

    const submit = e => {
        e.preventDefault()
        const value = code.trim()
        if (!value) {
            setError('Masukkan kode verifikasi terlebih dahulu.')
            return
        }
        navigate(`/signature/verify/${encodeURIComponent(value)}`)
    }

    const found = result && result.documentId
    const rows = found ? [
        ['Nomor SMKHP', result.smkhpNumber],
        ['Nomor Aju', result.documentNumber],
        ['Penandatangan', result.signerName],
        ['Tahap', stageLabel(result.signatureStage)],
        ['Waktu Tanda Tangan', formatDate(result.signedAt)],
        ['Subjek Certificate', result.certificateSubject],
        ['Serial Certificate', result.certificateSerial],
        ['Algoritma', result.signatureAlgorithm],
        ['Kode Verifikasi', result.verificationCode]
    ] : []

    return (
        <div className="d-flex flex-column min-vh-100">
            <nav className="navbar navbar-light bg-white fixed-top shadow-sm py-2">
                <div className="container">
                    <span className="navbar-brand d-flex align-items-center fw-bold" style={{ fontSize: '1.4rem' }}>
                        <img src={Logo2} style={{ width: '52px', marginRight: '12px' }} alt="Logo" />
                        <span className="text-dark">Badan Mutu KKP</span>
                    </span>
                    <span className="navbar-text fw-semibold text-secondary d-none d-sm-block">SIAPMUTU</span>
                </div>
            </nav>

            <main
                className="container-fluid flex-grow-1 pb-5"
                style={{ background: 'linear-gradient(135deg, #e0f2fe 0%, #f1f5f9 100%)', paddingTop: '90px' }}
            >
                <Row className="justify-content-center mx-0">
                    <Col md={10} lg={8} className="px-0 px-sm-2">
                        <Card className="shadow border-0 mb-3 rounded-3">
                            <Card.Header className="bg-white border-bottom py-3">
                                <Card.Title as="h4" className="text-primary fw-bold mb-1">Verifikasi Tanda Tangan Elektronik</Card.Title>
                                <div className="small text-muted">Periksa keaslian dokumen SMKHP yang ditandatangani secara elektronik di SIAPMUTU.</div>
                            </Card.Header>
                            <Card.Body>
                                <Form onSubmit={submit}>
                                    <Form.Label className="fw-semibold">Kode Verifikasi</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            value={code}
                                            onChange={e => setCode(e.target.value)}
                                            placeholder="Masukkan kode verifikasi dari dokumen / QR"
                                            disabled={loading}
                                        />
                                        <Button type="submit" disabled={loading} className="text-nowrap">
                                            <FaMagnifyingGlass className="me-1" />Periksa
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>

                        {loading && (
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <div className="small text-muted mt-2">Memverifikasi dokumen...</div>
                            </div>
                        )}

                        {!loading && error && <Alert variant="danger">{error}</Alert>}

                        {!loading && result && (
                            <Card className="shadow border-0 rounded-3">
                                <Card.Body>
                                    <div className={`d-flex align-items-center gap-3 p-3 rounded-3 mb-3 ${result.valid ? 'bg-success' : 'bg-danger'} bg-opacity-10`}>
                                        {result.valid
                                            ? <FaCircleCheck size={40} className="text-success flex-shrink-0" />
                                            : <FaCircleXmark size={40} className="text-danger flex-shrink-0" />}
                                        <div>
                                            <div className={`fw-bold fs-5 ${result.valid ? 'text-success' : 'text-danger'}`}>
                                                {result.valid ? 'Dokumen Valid' : 'Dokumen Tidak Valid'}
                                            </div>
                                            <div className="text-dark">{result.message || '-'}</div>
                                        </div>
                                    </div>

                                    {found && (
                                        <>
                                            <dl className="row mb-3">
                                                {rows.map(([label, value]) => (
                                                    <React.Fragment key={label}>
                                                        <dt className="col-sm-4 text-muted fw-normal">{label}</dt>
                                                        <dd className="col-sm-8 fw-semibold text-break">{value || '-'}</dd>
                                                    </React.Fragment>
                                                ))}
                                            </dl>
                                            {result.valid && (
                                                <Button
                                                    variant="outline-primary"
                                                    href={signatureService.verifiedDocumentUrl(result.verificationCode)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <FaFilePdf className="me-1" />Lihat Dokumen Bertanda Tangan
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>
            </main>
        </div>
    )
}
